import { useCallback } from 'react'
import { useParams } from 'react-router'
import { ACKNOWLEDGEMENTS } from 'virtual:acknowledgements'
import { emit } from '../../platform/analytics/analytics'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { BackLink } from '../../platform/primitives/BackLink'
import { LEGAL_DOCS } from './support'
import { useOnce } from '../../platform/analytics/useOnce'
import './help.css'
import './legal.css'

type Doc = 'privacy' | 'terms' | 'acknowledgements'

const BACK = <BackLink to="/legal">Legal</BackLink>

/**
 * `/legal` and `/legal/:doc` (M14-R4..R6). Privacy and Terms live on the
 * marketing site, which carries its own version and date (intake B1/B2), so
 * those pages name the document and link out. Acknowledgements are generated
 * from the lockfile at build time (`virtual:acknowledgements`). `ChromeLayout`
 * renders the `<h1>`; this starts at `<h2>`.
 */
export function Legal() {
  const doc = useParams()['*'] ?? ''
  if (doc === 'privacy' || doc === 'terms') return <External key={doc} doc={doc} />
  if (doc === 'acknowledgements') return <Acknowledgements />
  if (doc === '') return <Index />
  return (
    <div className="legal">
      <Panel className="legal__panel">
        <p className="legal__p">There&apos;s no legal document called that.</p>
      </Panel>
      {BACK}
    </div>
  )
}

function Index() {
  return (
    <div className="legal">
      <Panel className="legal__panel">
      <h2 className="legal__h">Legal</h2>
      <ul className="legal__list">
        <li className="legal__item">
          <InlineLink to="/legal/privacy" className="help__link">Privacy policy</InlineLink>
          <span className="legal__desc">How TrackE5 handles your data.</span>
        </li>
        <li className="legal__item">
          <InlineLink to="/legal/terms" className="help__link">Terms of service</InlineLink>
          <span className="legal__desc">The terms for using TrackE5.</span>
        </li>
        <li className="legal__item">
          <InlineLink to="/legal/acknowledgements" className="help__link">Acknowledgements</InlineLink>
          <span className="legal__desc">The open-source software TrackE5 is built on.</span>
        </li>
      </ul>
      </Panel>
    </div>
  )
}

function useViewed(kind: Doc) {
  useOnce(useCallback(() => emit('legal_viewed', { kind }), [kind]))
}

function External({ doc }: { doc: 'privacy' | 'terms' }) {
  useViewed(doc)
  const { title, href } = LEGAL_DOCS[doc]
  return (
    <div className="legal">
      <Panel className="legal__panel">
      <h2 className="legal__h">{title}</h2>
      <p className="legal__p">The current version, with its date, is published on tracke5.com.</p>
      <p className="legal__p">
        <InlineLink to={href} className="help__link" target="_blank" rel="noopener noreferrer">
          Read the {title.toLowerCase()}
          <span className="sr-only"> (opens in a new tab)</span>
        </InlineLink>
      </p>
      </Panel>
      {BACK}
    </div>
  )
}

function Acknowledgements() {
  useViewed('acknowledgements')
  const byLicence = new Map<string, typeof ACKNOWLEDGEMENTS>()
  for (const a of ACKNOWLEDGEMENTS) {
    byLicence.set(a.license, [...(byLicence.get(a.license) ?? []), a])
  }
  const groups = [...byLicence.entries()].sort(
    (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
  )
  return (
    <div className="legal">
      <Panel className="legal__panel">
      <h2 className="legal__h">Acknowledgements</h2>
      <p className="legal__p">
        TrackE5 is built on these {ACKNOWLEDGEMENTS.length} open-source packages. This list is generated from the
        app&apos;s dependency manifest each time it is built.
      </p>
      {groups.map(([license, packages]) => (
        <section key={license} className="legal__group">
          <div className="legal__group-head">
            <h3 className="legal__group-title">{license}</h3>
            <span className="legal__group-count">{packages.length}</span>
          </div>
          <ul className="legal__packages">
            {packages.map((p) => (
              <li key={`${p.name}@${p.version}`} className="legal__package">
                {p.name} {p.version}
              </li>
            ))}
          </ul>
        </section>
      ))}
      </Panel>
      {BACK}
    </div>
  )
}
